import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ProGate from "@/components/ProGate";
import {
  Sparkles, Send, Mail, Instagram, DollarSign, FileText,
  Heart, Newspaper, TrendingUp, Loader2, MessageSquare, Trash2,
} from "lucide-react";
import { toast } from "sonner";

// One-tap tools mapped to their canned prompt keys (backend TOOL_PROMPTS)
const TOOLS = [
  { key: "launch_email", label: "Launch email", icon: Mail },
  { key: "instagram_post", label: "Instagram post", icon: Instagram },
  { key: "product_description", label: "Product description", icon: FileText },
  { key: "price_suggestion", label: "Suggest a price", icon: DollarSign },
  { key: "thank_you_note", label: "Thank-you note", icon: Heart },
  { key: "press_release", label: "Press release", icon: Newspaper },
];

const WELCOME = {
  role: "assistant",
  content:
    "Hey — I'm your AddVendTour business co-founder. I know your profile, applications, and ratings, so ask me anything. Try one of the shortcuts on the right, or just tell me what you're working on.",
};

export default function VendorAssistant() {
  return (
    <ProGate
      feature="The Business Assistant"
      pitch="A private AI co-founder that knows your AddVendTour profile, applications, and ratings — and helps you write, price, forecast, and grow."
      perks={[
        "Unlimited chat with Claude, tuned to your AddVendTour data",
        "One-tap tools for launch emails, IG posts, product copy, and pricing math",
        "90-day cash flow forecast built from your approved bookings",
        "Everything you say stays private to your account",
      ]}
    >
      <VendorAssistantInner />
    </ProGate>
  );
}

function VendorAssistantInner() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [cashflow, setCashflow] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages, busy]);

  // Hydrate persisted conversation + cashflow in parallel on mount
  useEffect(() => {
    (async () => {
      try {
        const [hist, cf] = await Promise.allSettled([
          api.get("/assistant/history"),
          api.get("/vendor/cashflow-forecast"),
        ]);
        if (hist.status === "fulfilled") {
          const prior = hist.value.data?.messages || [];
          if (prior.length > 0) {
            setMessages([WELCOME, ...prior.map((m) => ({
              role: m.role, content: m.content, meta: m.meta || undefined,
            }))]);
          }
        }
        if (cf.status === "fulfilled") setCashflow(cf.value.data);
      } finally {
        setHydrating(false);
      }
    })();
  }, []);

  const send = async (opts = {}) => {
    if (busy) return;
    const message = opts.message ?? input;
    const tool = opts.tool ?? null;
    const toolLabel = opts.toolLabel ?? null;
    if (!message.trim() && !tool) return;
    // Push user turn (real text OR tool affordance) in a single state update
    const nextHistory = [...messages];
    if (message.trim()) {
      nextHistory.push({ role: "user", content: message });
    } else if (tool && toolLabel) {
      nextHistory.push({ role: "user", content: `→ ${toolLabel}`, meta: "tool" });
    }
    setMessages(nextHistory);
    setInput("");
    setBusy(true);
    try {
      // Server is now stateful — it loads history from the DB itself. We only
      // send the new turn (plus tool key if any).
      const { data } = await api.post("/assistant/chat", {
        message: message || "",
        tool: tool,
      });
      setMessages([...nextHistory, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
      setMessages([...nextHistory, {
        role: "assistant",
        content: "I hit a snag reaching my brain. Try again in a moment?",
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const runTool = (toolKey, label) => {
    send({ message: "", tool: toolKey, toolLabel: label });
  };

  const clearConversation = async () => {
    if (busy || clearing) return;
    // Nothing to wipe if the vendor never spoke
    if (messages.length <= 1) return;
    if (!window.confirm("Clear this whole conversation? It can't be undone.")) return;
    setClearing(true);
    try {
      await api.delete("/assistant/history");
      setMessages([WELCOME]);
      toast.success("Conversation cleared.");
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
    } finally {
      setClearing(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="vendor-assistant-page">
      <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
        <Sparkles className="h-3 w-3" /> Your AI co-founder
      </div>
      <h1 className="font-serif text-5xl mt-3">Business Assistant.</h1>
      <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
        A private, business-savvy assistant that knows your AddVendTour profile, your applications, and your ratings — and helps you write, price, forecast, and grow.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        {/* Left: chat */}
        <div
          data-testid="assistant-chat-panel"
          className="lg:col-span-2 card-warm rounded-lg overflow-hidden flex flex-col"
          style={{ backgroundColor: "#fff", minHeight: "560px", maxHeight: "72vh" }}
        >
          <div
            className="flex items-center justify-between px-5 py-2.5 border-b"
            style={{ borderColor: "rgba(0,0,0,0.06)", backgroundColor: "#fdfbf6" }}
          >
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {hydrating ? "Loading your conversation…"
                : messages.length > 1
                  ? "Picking up where you left off."
                  : "Start a fresh conversation."}
            </div>
            <button
              type="button"
              onClick={clearConversation}
              disabled={clearing || busy || messages.length <= 1}
              data-testid="assistant-clear-btn"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors hover:bg-[rgba(212,88,58,0.06)] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: "rgba(0,0,0,0.10)", color: "var(--terracotta)" }}
              title="Clear conversation"
            >
              {clearing
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Trash2 className="h-3 w-3" />}
              <span>Clear</span>
            </button>
          </div>
          <div
            ref={listRef}
            data-testid="assistant-messages"
            className="flex-1 overflow-y-auto p-5 space-y-4"
          >
            {hydrating ? (
              <div data-testid="assistant-hydrating" className="flex items-center gap-2 text-xs italic" style={{ color: "var(--ink-soft)" }}>
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading your conversation…
              </div>
            ) : messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} meta={m.meta} error={m.error} />
            ))}
            {busy && (
              <div data-testid="assistant-typing" className="flex items-center gap-2 text-xs italic" style={{ color: "var(--ink-soft)" }}>
                <Loader2 className="h-3 w-3 animate-spin" />
                Thinking…
              </div>
            )}
          </div>
          <div
            className="border-t p-3 flex items-end gap-2"
            style={{ borderColor: "rgba(0,0,0,0.08)", backgroundColor: "#fbf8f2" }}
          >
            <Textarea
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask anything — pricing, marketing, cash flow, tax, business questions…"
              rows={2}
              className="resize-none flex-1"
              disabled={busy}
              maxLength={2000}
            />
            <Button
              type="button"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="assistant-send-btn"
              className="btn-primary rounded-full h-10 w-10 p-0 inline-flex items-center justify-center"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right: tools + cash flow */}
        <aside className="space-y-6">
          <div
            data-testid="assistant-tools"
            className="card-warm rounded-lg p-5"
            style={{ backgroundColor: "#fff" }}
          >
            <div className="overline" style={{ color: "var(--gold)" }}>One-tap tools</div>
            <div className="font-serif text-xl mt-1">Get instant drafts.</div>
            <div className="mt-4 grid grid-cols-1 gap-2">
              {TOOLS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => runTool(key, label)}
                  disabled={busy}
                  data-testid={`assistant-tool-${key}`}
                  className="text-left flex items-center gap-2.5 text-sm px-3 py-2.5 rounded-md border transition-colors hover:bg-[rgba(31,93,52,0.05)] disabled:opacity-50"
                  style={{ borderColor: "rgba(0,0,0,0.08)" }}
                >
                  <Icon className="h-4 w-4" style={{ color: "var(--forest)" }} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {cashflow && (
            <div
              data-testid="assistant-cashflow-card"
              className="card-warm rounded-lg p-5"
              style={{
                backgroundColor: "#fff",
                borderLeft: "3px solid var(--forest)",
              }}
            >
              <div className="overline flex items-center gap-1.5" style={{ color: "var(--forest)" }}>
                <TrendingUp className="h-3 w-3" /> Cash flow · next 90d
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  data-testid="cashflow-confirmed"
                  className="font-serif text-3xl"
                  style={{ color: "var(--forest)" }}
                >
                  ${cashflow.total_booth_fees_confirmed.toFixed(0)}
                </span>
                <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                  in confirmed booth fees
                </span>
              </div>
              {cashflow.total_booth_fees_pending > 0 && (
                <div className="mt-1 text-xs" style={{ color: "var(--gold)" }}>
                  + ${cashflow.total_booth_fees_pending.toFixed(0)} pending approval
                </div>
              )}
              <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
                {cashflow.upcoming_count} confirmed · {cashflow.pending_count} pending
              </div>
              {cashflow.upcoming.length > 0 && (
                <ul className="mt-4 space-y-2 text-xs" data-testid="cashflow-upcoming-list">
                  {cashflow.upcoming.slice(0, 4).map((row) => (
                    <li
                      key={row.event_id}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="truncate">
                        <span className={row.status === "approved" ? "" : "opacity-60"}>
                          {row.title}
                        </span>
                        <span className="ml-2 opacity-60">{row.date}</span>
                      </span>
                      <span
                        className="whitespace-nowrap font-mono"
                        style={{ color: row.status === "approved" ? "var(--forest)" : "var(--ink-soft)" }}
                      >
                        ${row.booth_fee.toFixed(0)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {cashflow.upcoming_count === 0 && cashflow.pending_count === 0 && (
                <div className="mt-3 text-xs italic" style={{ color: "var(--ink-soft)" }}>
                  Apply to a few events and I'll start forecasting.
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Bubble({ role, content, meta, error }) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div
          data-testid="assistant-bubble-user"
          className="rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[80%] whitespace-pre-wrap"
          style={{
            backgroundColor: meta === "tool" ? "rgba(201,162,75,0.14)" : "var(--forest)",
            color: meta === "tool" ? "var(--gold)" : "#fff",
          }}
        >
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div
        data-testid="assistant-bubble-assistant"
        className="rounded-2xl rounded-bl-sm px-4 py-3 text-sm max-w-[85%] whitespace-pre-wrap"
        style={{
          backgroundColor: error ? "rgba(212,88,58,0.10)" : "#f5efe5",
          color: error ? "var(--terracotta)" : "var(--ink)",
        }}
      >
        <div className="flex items-start gap-2">
          <MessageSquare className="h-3.5 w-3.5 shrink-0 mt-1" style={{ color: "var(--terracotta)" }} />
          <div className="min-w-0" style={{ lineHeight: 1.5 }}>{content}</div>
        </div>
      </div>
    </div>
  );
}
